import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

const PRINT_BODY_CLASS = "panel-sticker-printing";
const PRINT_STYLE_ID = "panel-sticker-print-page-style";
export const PANEL_SCAN_PREFIX = "P-";

const injectPrintPageStyles = () => {
  if (document.getElementById(PRINT_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @media print {
      @page {
        size: 100mm 100mm;
        margin: 0;
      }

      html.panel-sticker-printing,
      body.panel-sticker-printing {
        width: 100mm !important;
        height: auto !important;
        min-height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
      }

      html.panel-sticker-printing #panel-sticker-print-sheet {
        display: block !important;
      }

      html.panel-sticker-printing .panel-sticker-page {
        display: flex !important;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100mm !important;
        height: 100mm !important;
        box-sizing: border-box;
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
        break-inside: avoid;
      }

      html.panel-sticker-printing .panel-sticker-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }
    }
  `;
  document.head.appendChild(style);
};

const removePrintPageStyles = () => {
  document.getElementById(PRINT_STYLE_ID)?.remove();
};

const formatMeasurement = (panel) => {
  const { measurement_length_1, measurement_length_2, measurement_thickness } =
    panel;

  if (
    measurement_length_1 == null ||
    measurement_length_2 == null ||
    measurement_thickness == null
  ) {
    return "—";
  }

  return `${measurement_length_1} x ${measurement_length_2} x ${measurement_thickness}`;
};

const formatNameLine = (panel) => {
  const slot = `${panel.component}/${panel.label}`;

  if (panel.panel_name) return `${panel.panel_name} Panel (${slot})`;

  return slot;
};

export const panelScanCode = (partItemId) => {
  const id = Number(partItemId);
  if (!Number.isInteger(id) || id < 1) return null;

  return `${PANEL_SCAN_PREFIX}${id}`;
};

export const panelStickerText = (panel, projectOrderIdentifier) => ({
  scanCode: panelScanCode(panel.part_item_id),
  has_minifix: panel.has_minifix,
  m_label: panel.m_label,
  projectLine: {
    heading: "Project No:",
    value: projectOrderIdentifier ?? "—",
  },
  detailLines: [
    {
      heading: "Assembly Label:",
      value: `#${panel.project_item_label}`,
      valueClassName: "panel-sticker-assembly-value",
    },
    {
      heading: "Measurement:",
      value: formatMeasurement(panel),
    },
    {
      heading: "Room Name:",
      value: panel.project_item_path || "—",
    },
    {
      heading: "Panel Name:",
      value: formatNameLine(panel),
    },
  ],
});

const buildBarcodeDataUrl = (value) => {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value, {
    format: "CODE128",
    displayValue: false,
    margin: 0,
    width: 1,
    height: 20,
  });

  return canvas.toDataURL("image/png");
};

const mLabelSortNumber = (mLabel) => Number(String(mLabel).replace("M-", ""));

const sortStickersByMLabel = (stickers) =>
  [...stickers].sort(
    (a, b) => mLabelSortNumber(a.m_label) - mLabelSortNumber(b.m_label)
  );

const enrichPanelStickersForPrint = async (stickers) =>
  Promise.all(
    stickers.map(async (sticker) => {
      const scanCode = sticker.scanCode;
      if (!scanCode) {
        return { ...sticker, qrDataUrl: null, barcodeDataUrl: null };
      }

      const qrDataUrl = await QRCode.toDataURL(scanCode, {
        width: 40,
        margin: 1,
      });
      const barcodeDataUrl = sticker.has_minifix
        ? buildBarcodeDataUrl(scanCode)
        : null;

      return { ...sticker, qrDataUrl, barcodeDataUrl };
    })
  );

export const printPanelStickerLabels = async ({
  stickers,
  onReady,
  onDone,
}) => {
  const stickerList = sortStickersByMLabel(
    Array.isArray(stickers) ? stickers : []
  );

  const enrichedStickers = await enrichPanelStickersForPrint(stickerList);

  onReady({ stickers: enrichedStickers });

  const cleanup = () => {
    document.documentElement.classList.remove(PRINT_BODY_CLASS);
    document.body.classList.remove(PRINT_BODY_CLASS);
    removePrintPageStyles();
    window.onafterprint = null;
    onDone();
  };

  window.onafterprint = cleanup;

  requestAnimationFrame(() => {
    injectPrintPageStyles();
    document.documentElement.classList.add(PRINT_BODY_CLASS);
    document.body.classList.add(PRINT_BODY_CLASS);
    requestAnimationFrame(() => {
      window.print();
    });
  });
};

export const printPanelStickerLabel = ({ sticker, onReady, onDone }) =>
  printPanelStickerLabels({
    stickers: sticker ? [sticker] : [],
    onReady,
    onDone,
  });
